import { addDefaultIfEmpty, insertAfterIndex, removeAtIndex, sum, updateAtIndex } from '../../helpers/array'
import { ceilingToPrecision, roundToPrecision } from '../../helpers/math'
import { uniqueId } from '../../helpers/uniqueId'

type Employee = {
  id: string,
  name: string
  role: string
  hoursWorkedPerYear: number
  unbillableHoursPerYear: number
  overtimeHours: number
  bonuses: number
}

export type HourlyEmployee = Employee & {
  hourlyWage: number
  overtimeMultiplier: number
}

export type SalaryEmployee = Employee & {
  annualSalary: number
}

export type Employees = {
  salary: SalaryEmployee[],
  hourly: HourlyEmployee[],
}

export type Category = 'SALARY' | 'HOURLY'

export const emptySalaryEmployee = (): SalaryEmployee => ({
  id: uniqueId(),
  name: '',
  role: '',
  annualSalary: 0,
  bonuses: 0,
  hoursWorkedPerYear: 0,
  overtimeHours: 0,
  unbillableHoursPerYear: 0
})

export const emptyHourlyEmployee = (): HourlyEmployee => ({
  id: uniqueId(),
  name: '',
  role: '',
  bonuses: 0,
  hoursWorkedPerYear: 0,
  overtimeHours: 0,
  unbillableHoursPerYear: 0,
  hourlyWage: 0,
  overtimeMultiplier: 1.5,
})

export const createDefaultEmployees = (): Employees => ({
  salary: [emptySalaryEmployee()],
  hourly: [emptyHourlyEmployee()]
})

const updateCategoryAtIndex = (employees: Employees, category: unknown, index: number, updater: (t: any) => any): Employees => {
  if (category === 'HOURLY') {
    return {
      ...employees,
      hourly: updateAtIndex(employees.hourly, index, updater)
    }
  }

  return {
    ...employees,
    salary: updateAtIndex(employees.salary, index, updater)
  }
}

export const updateName = (employees: Employees, category: Category, index: number, name: string): Employees =>
  updateCategoryAtIndex(employees, category, index, x => ({ ...x, name }))

export const updateRole = (employees: Employees, category: Category, index: number, role: string): Employees =>
  updateCategoryAtIndex(employees, category, index, x => ({ ...x, role }))

const ensureHoursWorkedPerYear = <E extends Employee>(x: E): E => {
  const hoursWorkedPerYear = Math.max(x.hoursWorkedPerYear, x.unbillableHoursPerYear + x.overtimeHours)
  return { ...x, hoursWorkedPerYear }
}

export const updateHoursWorkedPerYear = (employees: Employees, category: Category, index: number, hoursWorkedPerYear: number): Employees =>
  updateCategoryAtIndex(employees, category, index, x => ensureHoursWorkedPerYear({ ...x, hoursWorkedPerYear }))

export const updateUnbillableHoursPerYear = (employees: Employees, category: Category, index: number, unbillableHoursPerYear: number): Employees =>
  updateCategoryAtIndex(employees, category, index, x => ensureHoursWorkedPerYear({ ...x, unbillableHoursPerYear }))

export const updateOvertimeHours = (employees: Employees, category: Category, index: number, overtimeHours: number): Employees =>
  updateCategoryAtIndex(employees, category, index, x => ensureHoursWorkedPerYear({ ...x, overtimeHours }))

export const updateBonuses = (employees: Employees, category: Category, index: number, bonuses: number): Employees =>
  updateCategoryAtIndex(employees, category, index, x => ({ ...x, bonuses }))

export const updateHourlyWage = (employees: Employees, index: number, hourlyWage: number): Employees =>
  ({ ...employees, hourly: updateAtIndex(employees.hourly, index, x => ({ ...x, hourlyWage })) })

export const updateOvertimeMultiplier = (employees: Employees, index: number, overtimeMultiplier: number): Employees =>
  ({ ...employees, hourly: updateAtIndex(employees.hourly, index, x => ({ ...x, overtimeMultiplier })) })

export const updateAnnualSalary = (employees: Employees, index: number, annualSalary: number): Employees =>
  ({ ...employees, salary: updateAtIndex(employees.salary, index, x => ({ ...x, annualSalary })) })

export const addEmployee = (employees: Employees, category: Category, index: number): Employees => {
  if (category === 'HOURLY') {
    return {
      ...employees,
      hourly: insertAfterIndex(employees.hourly, index, emptyHourlyEmployee())
    }
  }
  return {
    ...employees,
    salary: insertAfterIndex(employees.salary, index, emptySalaryEmployee())
  }
}

export const removeEmployee = (employees: Employees, category: Category, index: number): Employees => {
  if (category === 'HOURLY') {
    return {
      ...employees,
      hourly: addDefaultIfEmpty(removeAtIndex(employees.hourly, index), emptyHourlyEmployee())
    }
  }
  return {
    ...employees,
    salary: addDefaultIfEmpty(removeAtIndex(employees.salary, index), emptySalaryEmployee())
  }
}

const roundToNearestCent = (dollars: number): number => roundToPrecision(dollars, 2)

export const calculateTotalHourlyCompensation = (employee: HourlyEmployee): number => {
  const regularHours = employee.hoursWorkedPerYear - employee.overtimeHours
  return roundToNearestCent(regularHours * employee.hourlyWage +
    employee.overtimeHours * employee.hourlyWage * employee.overtimeMultiplier +
    employee.bonuses)
}

export const calculateTotalSalaryCompensation = (employee: SalaryEmployee): number =>
  employee.annualSalary + employee.bonuses

const calculateTotalAnnualCostOfLabor = (employees: Employees): number => {
  return sum(employees.salary, x => calculateTotalSalaryCompensation(x)) +
    sum(employees.hourly, x => calculateTotalHourlyCompensation(x))
}

const calculateTotalAnnualHours = (employees: Employees): number => {
  return sum([...employees.salary, ...employees.hourly], x => x.hoursWorkedPerYear)
}

const calculateTotalUnbillableHours = (employees: Employees): number => {
  return sum([...employees.salary, ...employees.hourly], x => x.unbillableHoursPerYear)
}

const calculatePercentageOfUnbillableHours = (unbillableHours: number, annualHours: number): number => {
  if (annualHours === 0) {
    return 0
  }

  return roundToPrecision(unbillableHours / annualHours, 4)
}

const calculateManHourCost = (totalCost: number, hours: number): number => {
  if (hours === 0) {
    return 0
  }
  return ceilingToPrecision(totalCost / hours, 2)
}

const calculateTotalUnbillableCost = (employees: Employees) => {
  const hourlyUnbillable = sum(employees.hourly, x => x.unbillableHoursPerYear * x.hourlyWage + x.bonuses)
  const salaryCost = (employee: SalaryEmployee): number => employee.hoursWorkedPerYear === 0
    ? employee.annualSalary
    : (employee.annualSalary / employee.hoursWorkedPerYear) * employee.unbillableHoursPerYear
  const salaryUnbillable = sum(employees.salary, x => salaryCost(x) + x.bonuses)
  return hourlyUnbillable + salaryUnbillable
}

const calculateTotalOvertimeHours = (employees: Employees): number => {
  return sum([...employees.salary, ...employees.hourly], x => x.overtimeHours)
}

const calculateOvertimeFactor = (employees: Employees, annualHours: number): number => {
  if (annualHours === 0) {
    return 0
  }

  return roundToPrecision(calculateTotalOvertimeHours(employees) / annualHours, 4)
}

export type LaborStatistics = {
  annualCostOfLabor: number
  annualHours: number
  billableHours: number
  billableCost: number
  unbillableHours: number
  unbillablePercent: number
  unbillableCost: number
  manHourCost: number
  overtimeFactor: number
}

export const calculateLaborStatistics = (employees: Employees): LaborStatistics => {
  const annualCostOfLabor = calculateTotalAnnualCostOfLabor(employees)
  const annualHours = calculateTotalAnnualHours(employees)
  const unbillableHours = calculateTotalUnbillableHours(employees)
  const unbillableCost = calculateTotalUnbillableCost(employees)
  return {
    annualCostOfLabor,
    annualHours,
    billableHours: annualHours - unbillableHours,
    billableCost: annualCostOfLabor - unbillableCost,
    unbillableHours,
    unbillablePercent: calculatePercentageOfUnbillableHours(unbillableHours, annualHours),
    manHourCost: calculateManHourCost(annualCostOfLabor - unbillableCost, annualHours - unbillableHours),
    overtimeFactor: calculateOvertimeFactor(employees, annualHours),
    unbillableCost
  }
}

export const enforceInvariants = (employees: Employees | undefined): Employees => {
  if (!employees) {
    return createDefaultEmployees()
  }

  return {
    hourly: employees.hourly && employees.hourly.length > 0 ? employees.hourly.map(ensureHoursWorkedPerYear) : [emptyHourlyEmployee()],
    salary: employees.salary && employees.salary.length > 0 ? employees.salary.map(ensureHoursWorkedPerYear) : [emptySalaryEmployee()]
  }
}
